import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useUsersApi } from "../../http/users/api";
import Header from "../ui/Header";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import RoleAndPermissionsUserForm, { RoleAndPermissionsUserFormBody } from "./RolesAndPermissionsUserForm";
import TeamMembers from "./TeamMembers";
import UserForm, { UserFormBody } from "./UserForm";

const EditUser: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.users.edit} />
      <TabsProvider>
        <TabbedEditUser />
      </TabsProvider>
    </>
  );
};

export default EditUser;

const TabbedEditUser: FC = () => {
  const usersApi = useUsersApi();
  const userSettings = useUserSettings();
  const { id } = useParams<{ id: string }>();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userSwr = usersApi.getAdminUser(id!, aborter.current.signal);
  const [user, isInitialized] = useDataInitializer(userSwr.data, userSwr.error);

  useEffect(() => {
    id && userSwr.trigger();
  }, [id]);

  useEffect(() => {
    userSwr.error && dispatch(showErrorNotification(i18n, userSwr.error));
  }, [userSwr.error]);

  const onSubmit = async (formBody: UserFormBody) => {
    if (user?.id) {
      setIsSubmitting(true);
      try {
        await usersApi.updateAdminUser(user.id, {
          firstName: formBody.firstName,
          lastName: formBody.lastName,
          email: formBody.email,
          birthDate: formBody.birthDate,
          distanceInMiles: formBody.measurmentType?.value == "imperial",
          hidden: formBody.hidden,
          isRothoblaasUser: formBody.isRothoblaasUser,
          areNotificationsDisabled: formBody.areNotificationsDisabled,
        });
        dispatch(showSuccessNotification(i18n.t));
        userSwr.trigger();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const onSubmitRole = async (formBody: RoleAndPermissionsUserFormBody) => {
    if (user?.id) {
      const permRole = Array.from(new Set(formBody.roles.flatMap((r) => r.permissions)));

      const selPermission = formBody.permissions.filter((p) => !permRole.includes(p));

      setIsSubmitting(true);
      try {
        await usersApi.updateAdminUserRolesPermissions(user.id, {
          permissions: selPermission,
          roles: formBody.roles,
          userSubsidiariesIDs: formBody.userSubsidiariesIDs,
        });
        userSwr.trigger();
        dispatch(showSuccessNotification(i18n.t));
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const userForm = (
    <UserForm
      key="edit_user_form"
      onSubmit={onSubmit}
      user={user}
      isLoading={!isInitialized}
      isDisabled={!userSettings.permissions.users.update || isSubmitting || !user?.id}
      reloadUser={userSwr.trigger}
    />
  );

  const tabs = [{ title: i18n.t.common.generalData }];

  const tabComponents: TabContent[] = [
    {
      body: userForm,
      eager: true,
      withBorder: true,
    },
  ];

  if (userSettings.permissions.userRoles.read) {
    tabs.push({ title: i18n.t.common.rolesAndPermissions });
    tabComponents.push({
      body: (
        <RoleAndPermissionsUserForm
          key="role_permissions"
          user={user}
          onSubmit={onSubmitRole}
          isDisabled={
            !user || userSettings.me.id === user?.id || isSubmitting || !userSettings.permissions.userRoles.update
          }
        />
      ),
      withBorder: true,
    });
  }

  tabs.push({ title: i18n.t.common.teamMembers });
  tabComponents.push({
    body: <TeamMembers key="team_members" userId={id!} />,
    withBorder: true,
  });

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} />
    </>
  );
};
